import { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Add, Trash } from "iconsax-react";
import { EmptyState } from "@/shared/components";
import { Button, Input, Select, SearchableSelect } from "@/shared/ui";
import { LANGUAGE_OPTIONS, OTHER_LANGUAGE_VALUE } from "@/features/hr/hiring";
import type { LanguageRecord, LanguageProficiencyLevel } from "../../../types/personalSheet";
import { editLanguageEntrySchema } from "./validation";
import type { SectionFormHandle } from "./types";

interface Props {
  records: LanguageRecord[];
  recordsEditable?: boolean;
  draftAddedRecords?: LanguageRecord[];
}

interface LanguageEntry {
  id: number | null;
  language: string;
  proficiency_level: string;
  languageIsOther: boolean;
}

function makeEmptyEntry(): LanguageEntry {
  return { id: null, language: "", proficiency_level: "", languageIsOther: false };
}

function fromRecord(record: LanguageRecord): LanguageEntry {
  return {
    id: record.id,
    language: record.language,
    proficiency_level: record.proficiency_level,
    languageIsOther: record.language !== "" && !LANGUAGE_OPTIONS.some((o) => o.value === record.language),
  };
}

function isFilledEntry(entry: LanguageEntry): boolean {
  return entry.language.trim() !== "";
}

function toApiEntry(entry: LanguageEntry): Record<string, unknown> {
  const api: Record<string, unknown> = { language: entry.language, proficiency_level: entry.proficiency_level };
  if (entry.id != null) api.id = entry.id;
  return api;
}

function entriesSignature(entries: LanguageEntry[]): string {
  return JSON.stringify(entries.filter(isFilledEntry).map(toApiEntry));
}

const PROFICIENCY_LEVELS: LanguageProficiencyLevel[] = ["read_with_dictionary", "read_and_communicate", "fluent"];

const EditLanguagesSection = forwardRef<SectionFormHandle, Props>(function EditLanguagesSection({ records, recordsEditable = false, draftAddedRecords }, ref) {
  const { t } = useTranslation("EmployeePortal");

  const [newEntries, setNewEntries] = useState<LanguageEntry[]>(() =>
    recordsEditable ? records.map(fromRecord) : (draftAddedRecords ?? []).map(fromRecord),
  );
  const initialSignatureRef = useRef<string>(entriesSignature(recordsEditable ? records.map(fromRecord) : []));
  const [validationErrors, setValidationErrors] = useState<Record<number, Record<string, string>>>({});

  const levelOptions = PROFICIENCY_LEVELS.map((l) => ({ value: l, label: t(`personalSheet.proficiencyLevels.${l}`) }));
  const otherLanguageOption = { value: OTHER_LANGUAGE_VALUE, label: t("personalSheet.otherLanguage") };
  const allLanguageOptions = [...LANGUAGE_OPTIONS, otherLanguageOption];

  function handleLanguageSelect(index: number, option: { value: string; label: string } | null) {
    setNewEntries((prev) =>
      prev.map((entry, i) => {
        if (i !== index) return entry;
        if (option?.value === OTHER_LANGUAGE_VALUE) return { ...entry, languageIsOther: true, language: "" };
        return { ...entry, languageIsOther: false, language: option?.value ?? "" };
      }),
    );
  }

  function handleCustomLanguageChange(index: number, value: string) {
    setNewEntries((prev) => prev.map((entry, i) => (i === index ? { ...entry, language: value } : entry)));
  }

  function handleAddEntry() {
    setNewEntries((prev) => [...prev, makeEmptyEntry()]);
  }

  function handleRemoveEntry(index: number) {
    setNewEntries((prev) => prev.filter((_, i) => i !== index));
    setValidationErrors((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  }

  function updateEntry(index: number, field: "language" | "proficiency_level", value: string) {
    setNewEntries((prev) => prev.map((entry, i) => (i === index ? { ...entry, [field]: value } : entry)));
  }

  useImperativeHandle(ref, function buildHandle() {
    return {
      getChanges() {
        if (recordsEditable) {
          if (entriesSignature(newEntries) === initialSignatureRef.current) return null;
          return newEntries.filter(isFilledEntry).map(toApiEntry) as unknown as Record<string, unknown>;
        }
        if (!newEntries.some(isFilledEntry)) return null;
        const allRecords = [...records.map(fromRecord), ...newEntries.filter(isFilledEntry)].map(toApiEntry);
        return allRecords as unknown as Record<string, unknown>;
      },
      getFiles() {
        return null;
      },
      async validate() {
        const errors: Record<number, Record<string, string>> = {};
        let allValid = true;
        for (let i = 0; i < newEntries.length; i++) {
          const entry = newEntries[i];
          if (!isFilledEntry(entry)) continue;
          const result = editLanguageEntrySchema.safeParse({ language: entry.language, proficiency_level: entry.proficiency_level });
          if (!result.success) {
            allValid = false;
            const entryErrors: Record<string, string> = {};
            for (const issue of result.error.issues) entryErrors[issue.path[0] as string] = issue.message;
            errors[i] = entryErrors;
          }
        }
        const seenLanguages = new Map<string, number>();
        for (let i = 0; i < newEntries.length; i++) {
          const entry = newEntries[i];
          if (!isFilledEntry(entry)) continue;
          const key = entry.language.trim().toLowerCase();
          if (!key) continue;
          if (seenLanguages.has(key)) {
            allValid = false;
            errors[i] = { ...errors[i], language: "personalSheet.validation.duplicateLanguage" };
          } else {
            seenLanguages.set(key, i);
          }
        }
        setValidationErrors(errors);
        return allValid;
      },
      isDirty() {
        if (recordsEditable) return entriesSignature(newEntries) !== initialSignatureRef.current;
        return newEntries.some(isFilledEntry);
      },
    };
  });

  const showReadOnly = !recordsEditable;
  const visibleCount = (showReadOnly ? records.length : 0) + newEntries.length;

  return (
    <div className="flex flex-col gap-4">
      {visibleCount === 0 && (
        <EmptyState noDataText={t("personalSheet.empty.languages")} noDataHint={t("personalSheet.empty.languagesHint")} compact />
      )}
      {showReadOnly &&
        records.map((record, index) => (
          <div key={record.id} className="p-4 radius-md surface-secondary-fill flex flex-col gap-4">
            <p className="text-body-bold-md content-base-primary">{t("personalSheet.languageRecord", { number: index + 1 })}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input label={t("personalSheet.fields.language")} placeholder="" value={record.language} disabled />
              <Select
                label={t("personalSheet.fields.proficiencyLevel")}
                options={levelOptions}
                value={record.proficiency_level}
                onChange={() => {}}
                placeholder=""
                disabled
              />
            </div>
          </div>
        ))}

      {newEntries.map((entry, index) => (
        <div key={index} className="p-4 radius-md surface-secondary-fill flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <p className="text-body-bold-md content-base-primary">
              {t("personalSheet.languageRecord", { number: (showReadOnly ? records.length : 0) + index + 1 })}
            </p>
            <Button variant="danger" size="sm" onClick={() => handleRemoveEntry(index)}>
              <Trash size={16} color="currentColor" />
              {t("personalSheet.removeRecord")}
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <SearchableSelect
                label={t("personalSheet.fields.language")}
                hasRequiredLabel
                placeholder={t("personalSheet.fields.language")}
                options={allLanguageOptions}
                value={entry.languageIsOther ? otherLanguageOption : (allLanguageOptions.find((o) => o.value === entry.language) ?? null)}
                onChange={(option) => handleLanguageSelect(index, option)}
                searchKeys={["label"]}
                displayKey="label"
                isOptionEqual={(a, b) => a.value === b.value}
                error={validationErrors[index]?.language ? t(validationErrors[index].language) : undefined}
              />
              {entry.languageIsOther && (
                <Input
                  placeholder={t("personalSheet.languageOtherPlaceholder")}
                  value={entry.language}
                  onChange={(e) => handleCustomLanguageChange(index, e.target.value)}
                />
              )}
            </div>
            <Select
              label={t("personalSheet.fields.proficiencyLevel")}
              hasRequiredLabel
              options={levelOptions}
              value={entry.proficiency_level}
              onChange={(v) => updateEntry(index, "proficiency_level", v ?? "")}
              placeholder={t("personalSheet.fields.proficiencyLevel")}
              error={validationErrors[index]?.proficiency_level ? t(validationErrors[index].proficiency_level) : undefined}
            />
          </div>
        </div>
      ))}

      <Button variant="tertiary" size="lg" onClick={handleAddEntry} className="w-full text-center">
        <Add size={16} color="currentColor" />
        {t("personalSheet.addLanguage")}
      </Button>
    </div>
  );
});

export default EditLanguagesSection;
